import { useEffect, useState } from 'react';
import {
  FileText,
  Settings,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  LayoutDashboard,
} from 'lucide-react';
import dashboardService, {
  type DashboardStats,
  type OpsByStatusItem,
  type TopProductItem,
  type QualityAlertItem,
  type CriticalStockItem,
} from '@/services/dashboard';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { Card, CardHead } from '@/Components/ui/Card';
import { Button } from '@/Components/ui/Button';
import { StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';

const STATUS_LABELS: Record<string, string> = {
  ABERTA: 'Aberta',
  LIBERADA: 'Liberada',
  EM_PRODUCAO: 'Em Produção',
  PAUSADA: 'Pausada',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};

const STATUS_TONE: Record<string, Tone> = {
  ABERTA: 'info',
  LIBERADA: 'purple',
  EM_PRODUCAO: 'warning',
  PAUSADA: 'neutral',
  ENCERRADA: 'success',
  CANCELADA: 'danger',
};

export default function Dashboard() {
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    ops_abertas: 0,
    ops_em_producao: 0,
    ops_concluidas: 0,
    alertas_qualidade: 0,
  });
  const [opsByStatus, setOpsByStatus] = useState<OpsByStatusItem[]>([]);
  const [topProducts, setTopProducts] = useState<TopProductItem[]>([]);
  const [criticalStock, setCriticalStock] = useState<CriticalStockItem[]>([]);
  const [qualityAlerts, setQualityAlerts] = useState<QualityAlertItem[]>([]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsData, opsData, productsData, stockData, alertsData] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getOpsByStatus(),
        dashboardService.getTopProducts(5),
        dashboardService.getCriticalStock(10),
        dashboardService.getQualityAlerts(),
      ]);

      setStats(statsData);
      setOpsByStatus(opsData);
      setTopProducts(productsData);
      setCriticalStock(stockData);
      setQualityAlerts(alertsData);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  return (
    <div className="nx-page">
      <PageHeader
        icon={<LayoutDashboard size={21} strokeWidth={2} />}
        title="Dashboard de Produção"
        description="Visão geral da industrialização"
        actions={
          <Button type="button" variant="ghost" onClick={loadDashboard} loading={loading} loadingText="Atualizando...">
            <RefreshCw size={16} strokeWidth={2.2} />
            Atualizar
          </Button>
        }
      />

      <div className="nx-kpi-grid">
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">OPs Abertas</span>
            <div className="nx-kpi-icon">
              <FileText size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">{stats.ops_abertas || 0}</strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Em Produção</span>
            <div className="nx-kpi-icon">
              <Settings size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">{stats.ops_em_producao || 0}</strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Concluídas (Mês)</span>
            <div className="nx-kpi-icon">
              <CheckCircle2 size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">{stats.ops_concluidas || 0}</strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Alertas Qualidade</span>
            <div className="nx-kpi-icon">
              <AlertCircle size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value" style={{ color: stats.alertas_qualidade ? 'var(--nx-danger)' : undefined }}>
              {stats.alertas_qualidade || 0}
            </strong>
          </div>
        </article>
      </div>

      <div className="nx-grid-2">
        <Card className="nx-page-card">
          <CardHead title="Ordens de Produção por Status" />
          {loading ? (
            <LoadingBlock />
          ) : opsByStatus.length > 0 ? (
            <div className="nx-table-wrap">
              <table className="nx-table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Quantidade</th>
                  </tr>
                </thead>
                <tbody>
                  {opsByStatus.map((item) => (
                    <tr key={item.status}>
                      <td>
                        <StatusBadge
                          label={STATUS_LABELS[item.status] || item.status}
                          tone={STATUS_TONE[item.status] || 'neutral'}
                        />
                      </td>
                      <td>{item.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState message="Nenhum dado disponível." />
          )}
        </Card>

        <Card className="nx-page-card">
          <CardHead title="Produtos Mais Produzidos" />
          {loading ? (
            <LoadingBlock />
          ) : topProducts.length > 0 ? (
            <ul className="nx-stack nx-stack--md">
              {topProducts.map((produto, index) => (
                <li key={produto.produto__codigo} className="flex items-start gap-3">
                  <span className="text-xs font-bold opacity-60">#{index + 1}</span>
                  <div>
                    <p className="text-sm font-medium">
                      {produto.produto__codigo} - {produto.produto__descricao}
                    </p>
                    <p className="text-xs opacity-70">{produto.total} produzidos</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState message="Nenhum dado disponível." />
          )}
        </Card>
      </div>

      <Card className="nx-page-card">
        <CardHead
          title="Estoque Crítico"
          actions={<StatusBadge label={`${criticalStock.length} item(ns)`} tone="danger" />}
        />
        {loading ? (
          <LoadingBlock />
        ) : criticalStock.length > 0 ? (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Descrição</th>
                  <th>Estoque Atual</th>
                  <th>Estoque Mínimo</th>
                  <th>Unidade</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {criticalStock.map((item) => (
                  <tr key={item.codigo}>
                    <td>{item.codigo}</td>
                    <td>{item.descricao}</td>
                    <td>
                      <StatusBadge label={String(item.estoque_atual)} tone="danger" />
                    </td>
                    <td>{item.estoque_minimo}</td>
                    <td>{item.unidade}</td>
                    <td>
                      <StatusBadge label="Crítico" tone="danger" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState message="Tudo OK! Nenhum item com estoque crítico no momento." />
        )}
      </Card>

      {qualityAlerts.length > 0 && (
        <Card className="nx-page-card">
          <CardHead
            title="Alertas de Qualidade"
            actions={<StatusBadge label={`${qualityAlerts.length} alerta(s)`} tone="warning" />}
          />
          <ul className="nx-stack nx-stack--md">
            {qualityAlerts.map((alerta, index) => (
              <li key={index} className="flex items-start gap-3">
                {alerta.resultado === 'REPROVADO' ? (
                  <AlertCircle size={18} style={{ color: 'var(--nx-danger)' }} />
                ) : (
                  <CheckCircle2 size={18} style={{ color: 'var(--nx-ok)' }} />
                )}
                <div>
                  <p className="text-sm font-medium">
                    Lote: {alerta.lote__numero_lote} — {alerta.lote__produto__codigo} {alerta.lote__produto__descricao}
                  </p>
                  <p className="text-xs opacity-70">
                    {alerta.tipo} - {alerta.resultado} -{' '}
                    {alerta.data_inspecao ? new Date(alerta.data_inspecao).toLocaleString('pt-BR') : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function LoadingBlock() {
  return (
    <div className="nx-table-loading">Carregando…</div>
  );
}

